import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { supabaseHelpers } from '../lib/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUpload, FiX, FiLoader, FiAlertCircle } = FiIcons;

const ImageUpload = ({ images = [], onImagesChange, maxImages = 3 }) => {
  const { isDark } = useTheme();
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState('');
  const fileInputRef = React.useRef();

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    const remainingSlots = maxImages - images.length;
    const filesToAdd = files.slice(0, remainingSlots);

    if (filesToAdd.length === 0) {
      setError('Hai raggiunto il limite massimo di immagini');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const uploadPromises = filesToAdd.map(async (file) => {
        if (!file.type.startsWith('image/')) {
          throw new Error(`${file.name} non è un'immagine valida`);
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB
          throw new Error(`${file.name} è troppo grande (massimo 5MB)`);
        }

        console.log('Processing file:', file.name);
        
        const result = await supabaseHelpers.uploadImage(file);
        
        if (result.error) {
          throw result.error;
        }

        return {
          id: Date.now() + Math.random(),
          file,
          url: result.data.url,
          path: result.data.path,
          name: file.name
        };
      });

      const uploadedImages = await Promise.all(uploadPromises);
      
      console.log('All images uploaded successfully:', uploadedImages);
      
      onImagesChange(prev => [...prev, ...uploadedImages]);
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
    } catch (error) {
      console.error('Error uploading images:', error);
      setError(error.message || 'Errore durante il caricamento delle immagini');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = async (imageId) => {
    const imageToRemove = images.find(img => img.id === imageId);
    
    if (imageToRemove?.path) {
      try {
        await supabaseHelpers.deleteImage(imageToRemove.path);
      } catch (error) {
        console.error('Error deleting image:', error);
      }
    }

    onImagesChange(prev => prev.filter(img => img.id !== imageId));
    setError(''); // Clear any errors when removing images
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className={`p-3 rounded-lg flex items-center space-x-2 ${
          isDark ? 'bg-red-900/20 border border-red-600 text-red-400' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          <SafeIcon icon={FiAlertCircle} className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {images.map(image => (
          <div key={image.id} className="relative group">
            <div className={`relative rounded-lg overflow-hidden border-2 ${
              isDark ? 'border-gray-600' : 'border-gray-200'
            }`}>
              <img 
                src={image.url} 
                alt={image.name} 
                className="w-full h-32 object-cover"
                onError={(e) => {
                  console.error('Error loading image:', image.name);
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDIwMCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTI4IiBmaWxsPSIjRjNGNEY2Ii8+Cjx0ZXh0IHg9IjEwMCIgeT0iNjQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJjZW50cmFsIiBmaWxsPSIjNkI3MjgwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiPkVycm9yZSBjYXJpY2FtZW50bzwvdGV4dD4KPC9zdmc+';
                }}
              />
              <button
                onClick={() => removeImage(image.id)}
                disabled={uploading}
                className={`absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity ${
                  uploading ? 'cursor-not-allowed opacity-50' : 'hover:bg-red-600'
                }`}
              >
                <SafeIcon icon={FiX} className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}

        {images.length < maxImages && (
          <div
            onClick={() => !uploading && fileInputRef.current?.click()}
            className={`h-32 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors ${
              uploading 
                ? 'opacity-50 cursor-not-allowed' 
                : isDark 
                  ? 'border-gray-600 bg-gray-700 hover:bg-gray-600 text-gray-300' 
                  : 'border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-500'
            }`}
          >
            {uploading ? (
              <>
                <SafeIcon icon={FiLoader} className="w-8 h-8 mb-2 animate-spin" />
                <span className="text-sm font-medium">Caricamento...</span>
              </>
            ) : (
              <>
                <SafeIcon icon={FiUpload} className="w-8 h-8 mb-2" />
                <span className="text-sm font-medium">Carica foto</span>
                <span className="text-xs">({images.length}/{maxImages})</span>
              </>
            )}
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        disabled={uploading}
      />

      <div className="space-y-2">
        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Puoi caricare fino a {maxImages} foto per segnalazione. Formati supportati: JPG, PNG, WebP
        </p>
        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Dimensione massima per immagine: 5MB
        </p>
      </div>
    </div>
  );
};

export default ImageUpload;